/**
 * ⟦A-145 · C20⟧ `ChampBloc` : le champ nom / graine de la conversation, un bloc plat crème à
 * liseré sombre et ombre 3×3, VT323, curseur bloc simulé.
 *
 * Porté depuis `assets/making-of/2026-09-11-DA-creation/E/_commun.css` (`.champ-flottant`,
 * `.champ-bloc`, `.curseur-bloc`). Le vrai `<input>` reste (c'est un champ éditable, pas un
 * décor) : au focus, le curseur natif passe transparent et le bloc pixel `.curseur-bloc` prend
 * sa place, au blur `caret-color` revient à sa valeur normale (état remis à zéro, même si le
 * curseur natif ne se voit de toute façon jamais hors focus).
 *
 * Vérifié à l'écran (capture 390×844) : un premier jet laissait l'`<input>` en `flex: 1`, donc
 * plein largeur du bloc, et le curseur, posé APRÈS lui en DOM, atterrissait tout à droite de la
 * boîte crème, loin du texte, au lieu de le suivre. Corrigé par l'attribut `size` (nombre de
 * caractères, VT323 étant à chasse fixe) : l'`<input>` se dimensionne sur son contenu, comme la
 * maquette statique dont le bloc `.curseur-bloc` colle au dernier caractère, et le curseur
 * simulé retombe juste après. `onFocus` sélectionne tout le contenu (comme le champ nom l'a
 * toujours fait), donc taper remplace le texte d'un bloc et le curseur retombe en fin de valeur.
 */

import { useId, useState } from "react";

interface ChampBlocProps {
  label: string;
  value: string;
  onChange: (valeur: string) => void;
  aide?: string;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  invalide?: boolean;
  selectionnerAuFocus?: boolean;
}

export function ChampBloc({
  label,
  value,
  onChange,
  aide,
  placeholder,
  maxLength,
  autoFocus,
  invalide,
  selectionnerAuFocus,
}: ChampBlocProps) {
  const id = useId();
  const [focus, setFocus] = useState(false);

  return (
    <div className="champ-flottant">
      <label className="champ-flottant-label" htmlFor={id}>
        {label}
      </label>
      <div className={`champ-bloc${invalide ? " invalide" : ""}`}>
        <input
          id={id}
          className="champ-bloc-saisie"
          value={value}
          aria-invalid={invalide}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            setFocus(true);
            if (selectionnerAuFocus) e.target.select();
          }}
          onBlur={() => setFocus(false)}
          placeholder={placeholder}
          maxLength={maxLength}
          autoFocus={autoFocus}
          size={Math.max(value.length, placeholder?.length ?? 0, 3)}
          style={{ caretColor: focus ? "transparent" : undefined }}
        />
        {focus ? <span className="curseur-bloc" aria-hidden="true" /> : null}
      </div>
      {aide ? <span className="champ-flottant-aide">{aide}</span> : null}
    </div>
  );
}
